// Persists user-defined column mappings (file column name -> target field) so the
// ChatAssistant can auto-apply them the next time the same file (or a file with the
// same column shape) is uploaded. Backed by localStorage to keep things simple and
// to match how other parts of this app persist UI preferences (see SidebarNavigation).

const STORAGE_KEY = 'chatAssistant_fileMappings';
// Cap entries so the store can't grow unbounded if a user uploads many distinct files.
const MAX_SAVED_MAPPINGS = 50;

// Mirror of the ColumnMapping type used in ChatAssistant. Defined locally to avoid a
// circular import; the shape is intentionally simple (originalColumn -> target field
// or null/'ignore'/'').
export type SavedColumnMappings = Record<string, string | null>;

export interface SavedFileMapping {
    fileName: string;
    /** Sorted, lowercased pipe-joined column names of the file at save time. */
    columnSignature: string;
    columnMappings: SavedColumnMappings;
    /** ISO timestamp of last save/use, used for sorting + LRU eviction. */
    lastUsed: string;
    useCount: number;
}

export function buildColumnSignature(columns: string[]): string {
    return columns
        .map(c => String(c ?? '').trim().toLowerCase())
        .filter(c => c.length > 0)
        .sort()
        .join('|');
}

// Tolerant key form used when comparing/locating column names across uploads.
// Column headers can subtly drift between exports (case, surrounding whitespace),
// so we look them up by this normalized form rather than the raw key.
function normalizeColumnKey(name: string): string {
    return String(name ?? '').trim().toLowerCase();
}

/** Look up a saved target for a file column, ignoring case and surrounding whitespace. */
export function getSavedTarget(
    mapping: SavedFileMapping,
    columnName: string
): string | null | undefined {
    if (!mapping || !mapping.columnMappings) return undefined;
    if (Object.prototype.hasOwnProperty.call(mapping.columnMappings, columnName)) {
        return mapping.columnMappings[columnName];
    }
    const wanted = normalizeColumnKey(columnName);
    for (const [k, v] of Object.entries(mapping.columnMappings)) {
        if (normalizeColumnKey(k) === wanted) return v;
    }
    return undefined;
}

export function loadSavedMappings(): SavedFileMapping[] {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        // Be defensive about shape in case the stored format ever changes.
        return parsed.filter((m: any): m is SavedFileMapping =>
            m && typeof m.fileName === 'string'
            && typeof m.columnSignature === 'string'
            && m.columnMappings && typeof m.columnMappings === 'object'
        );
    } catch (err) {
        console.warn('[ChatAssistant] Failed to read saved file mappings', err);
        return [];
    }
}

function persistMappings(mappings: SavedFileMapping[]): void {
    try {
        // Sort newest-first and trim to the cap to keep the store bounded.
        const sorted = [...mappings].sort((a, b) => b.lastUsed.localeCompare(a.lastUsed));
        localStorage.setItem(STORAGE_KEY, JSON.stringify(sorted.slice(0, MAX_SAVED_MAPPINGS)));
    } catch (err) {
        console.warn('[ChatAssistant] Failed to persist file mappings', err);
    }
}

/**
 * Look up a previously-saved mapping for an uploaded file. Prefers an exact file-name
 * match, and falls back to a column-signature match so renamed files with the same
 * shape can still re-use the mapping. File-name matching is case-insensitive so
 * "trades.xlsx" and "Trades.xlsx" hit the same entry.
 */
export function findSavedMappingForFile(
    fileName: string,
    columns: string[]
): SavedFileMapping | undefined {
    const all = loadSavedMappings();
    if (all.length === 0) return undefined;

    if (fileName) {
        const wanted = fileName.toLowerCase();
        const byName = all.find(m => m.fileName.toLowerCase() === wanted);
        if (byName) return byName;
    }

    const signature = buildColumnSignature(columns);
    if (signature) {
        return all.find(m => m.columnSignature === signature);
    }
    return undefined;
}

/**
 * Save (or update) the mappings for a file. Mappings for columns that exist in the
 * current upload are replaced; mappings for columns NOT in the current upload are
 * preserved (so prior decisions for other versions of the file are kept). Pass the
 * FULL effective mapping (lazy-detected + user overrides) so the saved-mappings
 * dialog reflects how the file is actually being interpreted.
 */
export function recordSavedMapping(
    fileName: string,
    columns: string[],
    columnMappings: SavedColumnMappings
): void {
    if (!fileName) return;

    // Drop blank/undefined entries; we don't remember "user opened the dropdown
    // and left it empty". Note 'ignore' is intentionally preserved so re-uploads
    // stop prompting for columns the user has explicitly chosen to skip.
    const cleaned: SavedColumnMappings = {};
    for (const [col, target] of Object.entries(columnMappings)) {
        if (target !== null && target !== undefined && target !== '') {
            cleaned[col] = target;
        }
    }

    const all = loadSavedMappings();
    const signature = buildColumnSignature(columns);
    const existingIndex = all.findIndex(m => m.fileName.toLowerCase() === fileName.toLowerCase());
    const now = new Date().toISOString();

    let merged: SavedColumnMappings = cleaned;
    if (existingIndex >= 0) {
        // Preserve prior mappings for columns NOT in the current file (so other
        // versions of the file keep their choices). Compare via the normalized
        // key so casing/whitespace differences don't double-up entries.
        const currentColKeys = new Set(columns.map(normalizeColumnKey));
        const currentMappedKeys = new Set(Object.keys(cleaned).map(normalizeColumnKey));
        const carryOver: SavedColumnMappings = {};
        for (const [oldCol, oldTarget] of Object.entries(all[existingIndex].columnMappings)) {
            const k = normalizeColumnKey(oldCol);
            if (!currentColKeys.has(k) && !currentMappedKeys.has(k)) {
                carryOver[oldCol] = oldTarget;
            }
        }
        merged = { ...carryOver, ...cleaned };
    }

    if (Object.keys(merged).length === 0) return;

    if (existingIndex >= 0) {
        all[existingIndex] = {
            ...all[existingIndex],
            // Keep the original casing of the file name from the first save so
            // the bookmark list stays stable when the user re-uploads.
            columnSignature: signature || all[existingIndex].columnSignature,
            columnMappings: merged,
            lastUsed: now,
            useCount: (all[existingIndex].useCount || 0) + 1,
        };
    } else {
        all.push({
            fileName,
            columnSignature: signature,
            columnMappings: merged,
            lastUsed: now,
            useCount: 1,
        });
    }

    persistMappings(all);
}

export function deleteSavedMapping(fileName: string): void {
    const all = loadSavedMappings();
    const filtered = all.filter(m => m.fileName.toLowerCase() !== fileName.toLowerCase());
    persistMappings(filtered);
}

export function clearAllSavedMappings(): void {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch (err) {
        console.warn('[ChatAssistant] Failed to clear saved file mappings', err);
    }
}

